<template>
  <div>
    <div>
      <!-- category input -->
      <form.Field
        name="name"
        :validators="{
          onBlur: yup.string().required('Category is required'),
        }"
      >
        <template #default="{ field }">
          <BaseInput
            :name="field.name"
            :model-value="category.name"
            placeholder="Category"
            class="mt-4"
            :error="
              field.state.meta.errors &&
              (field.state.meta.errors[0] as string)
            "
            @blur="
              updateBudgetItemsCategory(
                $event,
                category.name,
                field.handleBlur
              )
            "
          />
        </template>
      </form.Field>

      <!-- change order line items -->
      <!-- line items -->
      <BaseDraggable
        :model-value="sortedLineItems"
        item-key="id"
        :items-id-prefix="`id${category.key}`"
        @update:model-value="reOrderItems"
        @delete="(event) => deleteItem(event.element.id)"
      >
        <template #delete-button="{ element }">
          <span
            class="i-heroicons-trash text-error"
            :class="
              sortedLineItems.length === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-error cursor-pointer'
            "
            :disabled="sortedLineItems.length === 1"
            @click="sortedLineItems.length === 1
                ? ''
                : deleteItem(element.id)
            "
          />
        </template>
        <template #collapse-button="{ open, element, toggleFn }">
          <span
            class="text-gray-600 size-4"
            :class="
              open
                ? 'i-mdi-unfold-less-horizontal'
                : 'i-mdi-unfold-more-horizontal'
            "
            @click="
              toggleFn(element.id);
              validate(element.value, open)
            "
          />
        </template>

        <template #collapsed-text="{ element }">
          <div class="flex item-center py-2">
            <span class="font-bold">{{ element.value.title }}</span>
          </div>
        </template>

        <template #item-content="{ element: item, index }">
          <div :id="`id${category.key}${index}`">
            <CreateBudgetItem
              :id="item.value.id"
              v-model="item.value"
              :is-saving="false"
              @can-submit="
                (value) => (itemsValidations[item.value.id] = value)
              "
              @on-delete="deleteItem(item.value.id)"
            />
          </div>
        </template>
      </BaseDraggable>

      <hr class="my-4" />
      <!-- add line buttons -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <BaseButton
            variant="base"
            type="button"
            class="w-full hover:!bg-gray-200"
            style="box-shadow: none !important"
            @click="addBudgetItem(category)"
          >
            <div class="flex items-center justify-center gap-2">
              <span class="i-heroicons-plus-circle size-8" />
              <span class="text-lg">Add line item</span>
            </div>
          </BaseButton>
        </div>

        <div>
          <BaseButton
            variant="base"
            type="button"
            class="w-full hover:!bg-gray-200"
            style="box-shadow: none !important"
            @click.prevent="showTemplatesModal(category)"
          >
            <div class="flex items-center justify-center gap-2">
              <span class="i-heroicons-plus-circle size-8" />
              <span class="text-lg">Add from template</span>
            </div>
          </BaseButton>
        </div>
      </div>

      <hr class="my-4" />
      <!-- save button -->
      <form.Subscribe>
        <template #default="{ canSubmit, isSubmitting }">
          <div class="flex gap-4">
            <BaseButton
              :disabled="isSubmitting"
              text="Cancel"
              :class="{
                'cursor-not-allowed opacity-50':isSubmitting,
              }"
              @click="emit('removeChangeOrder')"
            />

            <BaseButton
              :disabled="
                !canSubmit ||
                !isBudgetItemsFormsValid ||
                isSubmitting
              "
              text="Save Changes"
              type="submit"
              variant="primary"
              :class="{
                'cursor-not-allowed opacity-50':
                  !canSubmit ||
                  isSubmitting ||
                  !isBudgetItemsFormsValid
              }"
              @click="form.handleSubmit()"
            />
          </div>
        </template>
      </form.Subscribe>

    </div>
  </div>



  <!-- Budget items templates modal -->
  <Modal
    id="budgetTemplateModal"
    v-model="showModal"
    title="Apply Budget Template"
    :no-escape="true"
    :no-backdrop="true"
    :no-close-button="true"
    width="w-full md:w-[80%] lg:w-[70%] xl:w-[60%]"
  >
    <template #body>
      <BudgetItemTemplates
        @on-buget-template-selected="bugetTemplateSelected"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { BudgetItem } from '@/models/budget'
import BudgetItemTemplates from '../../Budget/components//BudgetItemTemplates.vue'
import CreateBudgetItem from '../../Budget/components/CreateBudgetItem.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import { Proposal, generateChangeOrderId } from '@/models/proposal'
import { omit, uniqueId } from 'underscore'
import { computed, PropType, ref } from 'vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { toNumber } from '@/utils'
import BaseDraggable from '@/components/base/BaseDraggable.vue'
import Modal from '@/components/app/AppModal.vue'
import { useAuth } from '@/composables/useAuth'

const emit = defineEmits(['saveChangeOrder', 'removeChangeOrder'])

const props = defineProps({
  customerId: { type: String, required: true },
  projectId: { type: String, required: true },
  categoryWeight: { type: Number, required: true },
  defaultCategoryName: { type: String, required: false },
  proposal: { type: Object as PropType<Proposal>, required: true },
  saveFunction: { type: Function, required: true }
})

const category = ref({
  name: props.defaultCategoryName,
  weight: props.categoryWeight,
  key: uniqueId(),
})

const lineItems = ref<BudgetItem[]>([
  {
    ...new BudgetItem(),
    id: `${new Date().getTime()}-generatedID`,
    category: category.value,
  }
])
const itemsValidations = ref({})
const budgetTemplateCategory = ref(null)
const showModal = ref(false)
const { currentUser } = useAuth()

const form = useForm({
  defaultValues: category.value,
  validatorAdapter: yupValidator(),
  onSubmit: async () => {
    const proposal = new Proposal()
    proposal.customerId = props.customerId
    proposal.contractDate = new Date()
    proposal.projectId = props.projectId
    proposal.budgetId = props.projectId
    proposal.preparerId = currentUser.value.id
    proposal.termsConditions = props.proposal.termsConditions
    proposal.contractId = await generateChangeOrderId(props.projectId)
    proposal.lineItems = lineItems.value.map((item) => item.id)

    const payload = { proposal, lineItems: lineItems.value }
    await props.saveFunction(payload)
  },
})

const updateBudgetItemsCategory = ($event, oldCategoryName, handleBlur) => {
  lineItems.value.forEach((item) => {
    if (item.category.name === oldCategoryName) {
      item.category.name = $event.target.value
      item.totalPrice =
        toNumber(item.totalPrice) === 0
          ? item.totalPrice === 0
            ? 0.0
            : 0
          : item.totalPrice
    }
  })
  handleBlur()
}

const sortedLineItems = computed(() => {
  return lineItems.value.map((item) => {
    return {
      value: item,
      id: item.id,
    }
  })
})

const reOrderItems = (newItems) => {
  newItems.forEach((item, index) => {
    const budgetItem = lineItems.value.find(
      (lineItem) => lineItem.id === item.value.id,
    )
    budgetItem.weight = index + 1
  })
  lineItems.value = lineItems.value.sort((a, b) => a.weight - b.weight)
}

const deleteItem = (itemId) => {
  const index = lineItems.value.findIndex((item) => item.id === itemId)

  if (index > -1) {
    lineItems.value.splice(index, 1)
    itemsValidations.value = omit(itemsValidations.value, itemId)
  }
}

const validate = (item, open) => {
  if (open) {
    item.totalPrice =
      toNumber(item.totalPrice) === 0
        ? item.totalPrice === 0
          ? 0.0
          : 0
        : item.totalPrice
  }
}

const addBudgetItem = (category) => {
  const budgetItem = new BudgetItem()
  budgetItem.id = `${new Date().getTime()}-generatedID`
  budgetItem.materialsBudget = 0
  budgetItem.laborBudget = 0
  budgetItem.category = category
  budgetItem.weight = nextLineItemWeight(category)
  lineItems.value.push(Object.assign({}, budgetItem))
}

const nextLineItemWeight = (category) => {
  const items = lineItems.value.filter(
    (item) =>
      item.category.name === category.name &&
      item.category.weight === category.weight,
  )
  return items.length ? Math.max(...items.map((item) => item.weight)) + 1 : 1
}

const showTemplatesModal = (category) => {
  budgetTemplateCategory.value = category
  showModal.value = true
}

const bugetTemplateSelected = (items: BudgetItem[]) => {
  items.forEach((item) => {
    const budgetItem = Object.assign({}, JSON.parse(JSON.stringify(item)))
    budgetItem.id = `${new Date().getTime() + uniqueId()}-generatedID`
    budgetItem.category = budgetTemplateCategory.value
    budgetItem.weight = nextLineItemWeight(budgetTemplateCategory.value)
    lineItems.value.push(budgetItem)
  })
  showModal.value = false
}

const isBudgetItemsFormsValid = computed(() => {
  return Object.values(itemsValidations.value).every((value) => value)
})
</script>

